import React, { useMemo, useState } from "react";
import hasContainer from "@root/helpers/has-container";
import SearchIcon from "@root/assets/svgs/search.svg";

const STEP_COPY = {
    make: {
        step: "Make",
        placeholder: "Search vehicle makes",
        subtitle: "Choose a vehicle make or search the complete list to start with compatible cover options.",
        empty: "No matching makes found.",
    },
    model: {
        step: "Model",
        placeholder: "Search vehicle models",
        subtitle: "Choose a vehicle model or search the complete list to start with compatible cover options.",
        empty: "No matching models found.",
    },
    year: {
        step: "Year",
        placeholder: "Search years",
        subtitle: "Choose a year or search the complete list to start with compatible cover options.",
        empty: "No matching years found.",
    },
    option: {
        step: "Option",
        placeholder: "Search options",
        subtitle: "Choose an option or search the complete list to start with compatible cover options.",
        empty: "No matching options found.",
    },
};

const getItemConfig = (items = []) => {
    const first = items[0] || {};

    if (first.option_slug) return { key: "option_slug", labelKey: "option", step: "option" };
    if (first.option) return { key: "option", labelKey: "option", step: "option" };
    if (first.year) return { key: "year", labelKey: "year", step: "year" };
    if (first.make) return { key: "slug", labelKey: "make", step: "make" };

    return { key: "slug", labelKey: "model", step: "model" };
};

const getProductLabel = (basePath) => {
    const slug = String(basePath || "").split("/").filter(Boolean)[0] || "car-covers";

    return slug
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const YmmSectionModule = ({ data }) => {
    const items = data?.items || [];
    const [query, setQuery] = useState("");

    const { key, labelKey, step } = useMemo(() => getItemConfig(items), [items]);
    const copy = STEP_COPY[step] || STEP_COPY.make;

    const sortedItems = useMemo(() => (
        [...items].sort((a, b) => String(a?.[labelKey] || "").localeCompare(String(b?.[labelKey] || ""), undefined, {
            numeric: true,
            sensitivity: "base",
        }))
    ), [items, labelKey]);

    const filteredItems = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) return sortedItems;

        return sortedItems.filter((item) => String(item?.[labelKey] || "").toLowerCase().includes(normalizedQuery));
    }, [sortedItems, query, labelKey]);

    if (!data || items.length === 0) return null;

    const searchId = `ymm-section-search-${data._key || step}`;
    const title = `Find ${getProductLabel(data.basePath)} by ${copy.step}`;
    const subtitle = data.subtitle || copy.subtitle;
    const containerClass = hasContainer(data?.moduleSettings?.hasContainer) !== "container"
        ? "p-0"
        : "container";

    return (
        <div className="ymm-section">
            <div className={`${containerClass} ymm-section__content`}>
                <header className="ymm-section__header">
                    <h2 className="ymm-section__title">{title}</h2>
                    {subtitle && <p className="ymm-section__subtitle">{subtitle}</p>}
                </header>

                <div className="ymm-section__search">
                    <label className="ymm-section__search-label" htmlFor={searchId}>
                        {copy.placeholder}
                    </label>
                    <img src={SearchIcon} alt="" className="ymm-section__search-icon" />
                    <input
                        id={searchId}
                        className="ymm-section__search-input"
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={copy.placeholder}
                        autoComplete="off"
                        spellCheck="false"
                    />
                </div>

                <div className="ymm-section__card">
                    {filteredItems.length > 0 ? (
                        <ul className="ymm-section__items">
                            {filteredItems.map((item, index) => {
                                const itemKey = item[key] || `${item[labelKey]}-${index}`;
                                const url = [`${data.basePath}`, item[key]].filter(Boolean).join("/");

                                return (
                                    <li key={itemKey} className="ymm-section__item">
                                        <a href={url} className="ymm-section__link">
                                            {item[labelKey]}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="ymm-section__empty">{copy.empty}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default YmmSectionModule;
